import L from "@/modules/login/Login.module.css";

import { WButton } from "@/components/button/WButton.tsx";
import { FormSubmitButton } from "@/components/forms/FormSubmitButton.tsx";
import { WHookForm } from "@/components/forms/WHookForm.tsx";
import { Loading } from "@/components/loading/Loading.tsx";
import { useConfig } from "@/config.ts";
import { useAnalytics } from "@/modules/analytics/analytics.ts";
import { getResponseData } from "@/modules/api/apiClient.ts";
import { apikeyAtom } from "@/modules/api/apikey.store.ts";
import { unauthApiClientAtom } from "@/modules/api/createApiClient.ts";
import { notifyError, notifyErrorCustom } from "@/modules/error/errorReporting.ts";
import { InputRow } from "@/modules/login/InputRow.tsx";
import { LoginBox, LoginLayout } from "@/modules/login/LoginLayout.tsx";
import {
    isAuth0Error,
    type Auth0CallbackError,
    type Auth0CallbackResult,
    type Auth0CallbackSuccess,
} from "@/modules/login/auth0/auth0Callback.ts";
import { auth0ProviderAtom, decodeState } from "@/modules/login/auth0/auth0Provider.ts";
import type { SignupResult } from "@/modules/login/authProvider.ts";
import { getCurrentPath, getFullPath } from "@/modules/main/urls.ts";
import { errorToReactMessage } from "@/modules/notifications/errorToReactMessage.tsx";
import { isBuiltRelease } from "@/utils/environment.ts";
import { jsonEncodedBody } from "@/utils/fetchClient.ts";
import { useSimpleMutation } from "@/utils/react/useSimpleMutation.tsx";
import { Trans, useLingui } from "@lingui/react/macro";
import { useRouter } from "@tanstack/react-router";
import { useAtomValue, useStore } from "jotai/react";
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-aria-components";
import { useForm, type SubmitHandler } from "react-hook-form";
import invariant from "tiny-invariant";

interface Inputs {
    username: string;
    password: string;
}

export interface AfterLoginProps {
    onPostSignup: (result: SignupResult) => void;
}

export interface Auth0LoginFormProps extends AfterLoginProps {
    isSignup: boolean;
    defaultUsername?: string;
}

export function Auth0LoginForm({ isSignup, onPostSignup, defaultUsername }: Auth0LoginFormProps) {
    //#region Hooks
    const { t } = useLingui();
    const analytics = useAnalytics();
    const { termsUrl } = useConfig();

    const form = useForm<Inputs>({ defaultValues: { username: defaultUsername } });
    const authProvider = useAtomValue(auth0ProviderAtom);
    const [error, setError] = useState<ReactNode>(null);
    //#endregion

    const onSubmit: SubmitHandler<Inputs> = async ({ username, password }) => {
        const redirectPath = getCurrentPath();
        try {
            if (isSignup) {
                analytics.event({ event: "generate_lead", lead_type: "sign_up" });

                const result = await authProvider.signup({ username, password, redirectPath });
                onPostSignup(result);
            } else {
                await authProvider.login({ username, password, redirectPath });
            }
        } catch (error) {
            notifyError(error, { tags: ["Auth0LoginForm"] });
            setError(errorToReactMessage(error));
        }
    };

    return (
        <WHookForm form={form} onSubmit={onSubmit}>
            <InputRow name="username" label={t`E-mail`} type="email" autoComplete="username" autoFocus />
            <InputRow name="password" label={t`Password`} type="password" autoComplete="password" />

            {!!termsUrl?.url && isSignup && (
                <p className="pb-2 font-size-small">
                    <Trans>
                        By signing up, you agree to the{" "}
                        <Link href={termsUrl?.url} target="_blank">
                            Terms of Service
                        </Link>
                    </Trans>
                </p>
            )}

            <FormSubmitButton size="lg" fontSize="font-size-subtitle" className={L.Button}>
                {isSignup ? t`Sign up` : t`Log in`}
            </FormSubmitButton>

            {error && (
                <div className="react-aria-FieldError pt-2">
                    <b>Error:</b> {error}
                </div>
            )}
        </WHookForm>
    );
}

function getReturnPath({ state }: Auth0CallbackResult) {
    return decodeState(state) ?? "/";
}

export function Auth0Callback({ params }: { params: Auth0CallbackResult }) {
    const content = !isAuth0Error(params) ? <Auth0DoLogin params={params} /> : <Auth0ShowError params={params} />;

    return <LoginLayout>{content}</LoginLayout>;
}

function Auth0ShowError({ params }: { params: Auth0CallbackError }) {
    const { t } = useLingui();
    const { error, error_description } = params;
    const isEmailError = isVerifyEmailError(params);

    //#region Hooks
    useEffect(() => {
        if (isEmailError) {
            return;
        }

        notifyErrorCustom(`Auth0 callback error: ${error_description}`, {
            name: "Auth0Callback",
            context: { params },
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    //#endregion

    const returnPath = getReturnPath(params);
    const router = useRouter();
    const backLink = (
        <WButton
            className={L.More}
            action={async () => await router.navigate({ href: getFullPath(returnPath) })}
            label={t({ comment: "Button action", message: "Back" })}
        />
    );

    return (
        <LoginBox
            title={
                isEmailError
                    ? t({ comment: "Login page title when email verification is required", message: "Action required" })
                    : t`Login error`
            }
        >
            <div className="flex flex-col items-center gap-4">
                {!isEmailError && (
                    <div>
                        <b>
                            <Trans comment="Label for an authentication error code">Code:</Trans>
                        </b>{" "}
                        {error}
                    </div>
                )}
                <div>{error_description}</div>
                {backLink}
            </div>
        </LoginBox>
    );
}

function isVerifyEmailError({ error, error_description }: Auth0CallbackError) {
    return error === "access_denied" && /\bverify\b.*\bemail\b/i.test(error_description);
}

function Auth0DoLogin({ params }: { params: Auth0CallbackSuccess }) {
    /** If `false`, Auth0 debug information will be shown and the user must click a button to proceed. Useful for development. */
    const autoProceed = isBuiltRelease;

    //#region Hooks
    const { t } = useLingui();
    const analytics = useAnalytics();
    const router = useRouter();
    const store = useStore();

    async function onLogin(apikey: string) {
        store.set(apikeyAtom, apikey);
        const targetUrl = getFullPath(getReturnPath(params));
        await router.navigate({ href: targetUrl, replace: true });
    }

    const loginMutation = useSimpleMutation(async () => {
        const client = store.get(unauthApiClientAtom);

        // Forward the Auth0 data to the backend
        const data = getResponseData(
            await client.POST("/user-resource/auth0_callback", {
                ...jsonEncodedBody,
                body: params,
            }),
        );

        // TODO: Send `method` if this was social login (based on server response)
        analytics.event({
            event: data.is_sign_up ? "sign_up" : "login",
        });

        // Log in with the returned API key
        // (or show debug information in dev mode)
        if (autoProceed) {
            await onLogin(data.apikey);
        }
        return data;
    });

    useEffect(() => {
        // Start login when this component is loaded
        if (loginMutation.state === "idle") {
            loginMutation.mutate();
        }
    }, [loginMutation]);

    //#endregion

    async function manualProceed() {
        invariant(loginMutation.state === "hasData");
        await onLogin(loginMutation.data.apikey);
    }

    return (
        <LoginBox title={`${t`Logging in`}...`}>
            <div className="children:mb-4">
                {loginMutation.state === "loading" && <Loading icon="none" />}
                {loginMutation.state === "hasError" && (
                    <div className="color-text bg-red-1 rounded p-2">{errorToReactMessage(loginMutation.error)}</div>
                )}
                {loginMutation.state === "hasData" && !autoProceed && (
                    <WButton color="primary" label="Enter the zone" action={manualProceed} />
                )}
            </div>
        </LoginBox>
    );
}
